import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('vault_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('vault_token');
      localStorage.removeItem('vault_user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const authApi = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
  updateMe: (data) => api.put('/auth/me', data),
};

// Moto
export const motoApi = {
  getAll: () => api.get('/moto'),
  getById: (id) => api.get(`/moto/${id}`),
  create: (data) => api.post('/moto', data),
  update: (id, data) => api.put(`/moto/${id}`, data),
  delete: (id) => api.delete(`/moto/${id}`),
};

// Manutenzione
export const manutenzioneApi = {
  getAll: (motoId) => api.get('/manutenzione', { params: { moto_id: motoId } }),
  create: (data) => api.post('/manutenzione', data),
  update: (id, data) => api.put(`/manutenzione/${id}`, data),
  delete: (id) => api.delete(`/manutenzione/${id}`),
};

// Modifiche
export const modificheApi = {
  getAll: (motoId) => api.get('/modifiche', { params: { moto_id: motoId } }),
  create: (data) => api.post('/modifiche', data),
  update: (id, data) => api.put(`/modifiche/${id}`, data),
  delete: (id) => api.delete(`/modifiche/${id}`),
};

// Viaggi
export const viaggiApi = {
  getAll: (motoId) => api.get('/viaggi', { params: { moto_id: motoId } }),
  getById: (id) => api.get(`/viaggi/${id}`),
  create: (data) => api.post('/viaggi', data),
  update: (id, data) => api.put(`/viaggi/${id}`, data),
  delete: (id) => api.delete(`/viaggi/${id}`),
};

// Equipaggiamento
export const equipaggiamentoApi = {
  getAll: () => api.get('/equipaggiamento'),
  create: (data) => api.post('/equipaggiamento', data),
  update: (id, data) => api.put(`/equipaggiamento/${id}`, data),
  delete: (id) => api.delete(`/equipaggiamento/${id}`),
};

// Spese
export const speseApi = {
  getAll: (motoId) => api.get('/spese', { params: { moto_id: motoId } }),
  create: (data) => api.post('/spese', data),
  update: (id, data) => api.put(`/spese/${id}`, data),
  delete: (id) => api.delete(`/spese/${id}`),
  getFuelStats: (motoId) => api.get('/spese/fuel-stats', { params: { moto_id: motoId } }),
  getReport: (period, motoId) => api.get('/spese/report', { params: { period, moto_id: motoId } }),
};

// Promemoria
export const promemoriaApi = {
  getAll: (motoId) => api.get('/promemoria', { params: { moto_id: motoId } }),
  create: (data) => api.post('/promemoria', data),
  update: (id, data) => api.put(`/promemoria/${id}`, data),
  delete: (id) => api.delete(`/promemoria/${id}`),
};

// Documenti
export const documentiApi = {
  getAll: () => api.get('/documenti'),
  create: (data) => api.post('/documenti', data),
  update: (id, data) => api.put(`/documenti/${id}`, data),
  delete: (id) => api.delete(`/documenti/${id}`),
};

// Stats
export const statsApi = {
  getStats: (motoId) => api.get('/stats', { params: { moto_id: motoId } }),
  getSpesePerCategoria: () => api.get('/stats/spese-per-categoria'),
  getKmPerMoto: () => api.get('/stats/km-per-moto'),
};

// Exchange Rates
export const exchangeRatesApi = {
  getRates: () => api.get('/exchange-rates'),
};

// Route Planning
export const routeApi = {
  calculate: (data) => api.post('/route/calculate', data),
  geocode: (data) => api.post('/route/geocode', data),
};

// Favorite Routes
export const favoriteRoutesApi = {
  getAll: () => api.get('/favorite-routes'),
  create: (data) => api.post('/favorite-routes', data),
  delete: (id) => api.delete(`/favorite-routes/${id}`),
};

// Subscription & Payments
export const subscriptionApi = {
  getPlans: () => api.get('/subscription/plans'),
  getStatus: () => api.get('/subscription/status'),
  getTrialStatus: () => api.get('/subscription/trial-status'),
  getRenewalReminders: () => api.get('/subscription/renewal-reminders'),
  cancel: () => api.post('/subscription/cancel'),
  createCheckout: (data) => api.post('/payments/create-checkout', data),
  getPaymentStatus: (sessionId) => api.get(`/payments/status/${sessionId}`),
  getPaymentHistory: () => api.get('/payments/history'),
};

// Fuel Stations & Prices
export const fuelApi = {
  searchStations: (data) => api.post('/fuel-stations/search', data),
  searchPrices: (lat, lon, radius = 10000) => api.get('/fuel-prices/search', { 
    params: { lat, lon, radius } 
  }),
  // Favorite stations
  getFavorites: () => api.get('/favorite-stations'),
  addFavorite: (station) => api.post('/favorite-stations', station),
  removeFavorite: (stationId) => api.delete(`/favorite-stations/${stationId}`),
  checkFavorite: (stationId) => api.get(`/favorite-stations/check/${stationId}`),
};

export default api;
